import { toMuxDataEngineOptions } from "./engine.js";
import { getPlayerVersion } from "./env.js";
import { isEngineAdapter, isMediaSourceCapable } from "@videojs/media";
import { getMediaAdapter, getMediaElement } from "@videojs/media/dom";
import { listen } from "@videojs/utils/dom";
import Mux from "mux-embed";
//#region src/extension.ts
const MUX_VIDEO_DOMAIN = "mux.com";
/**
* The JS engine behind `media`, if it (or the adapter it fronts) drives one. Engines are unrelated types (an hls.js
* instance, a dash.js player, an SPF composition); which of them Mux Data can hook is decided by
* {@link toMuxDataEngineOptions}, not here.
*/
function engineOf(media) {
	const adapter = getMediaAdapter(media) ?? media;
	return isEngineAdapter(adapter) ? adapter.engine : null;
}
/**
* The media's source URL as the player sees it. An adapter reports the URL it plays on `src`, not the `blob:` it feeds
* the element. A plain `<video>` that picks from `<source>` children leaves `src` empty and reports the pick on
* `currentSrc`; a `blob:` there is a MediaSource handle, not a video, so it does not count.
*/
function srcOf(media) {
	if (!isMediaSourceCapable(media)) return "";
	if (media.src) return media.src;
	return media.currentSrc.startsWith("blob:") ? "" : media.currentSrc;
}
/**
* Player extension that monitors the player's media with the Mux Data SDK.
*
* The SDK needs the native element, so a media component or adapter is resolved to the `<video>` it fronts; a plain
* `<video>` is monitored directly. Source and engine changes are read from the media the player attached.
*
* Its `PlayerExtension` members are internal: the player drives them, and the element and hook that register it check
* that it conforms.
*/
var MuxDataExtension = class MuxDataExtension {
	static defaultProps = {
		MuxDataSdk: Mux,
		beaconCollectionDomain: void 0,
		debug: false,
		disableCookies: false,
		envKey: void 0,
		playerSoftwareName: void 0,
		playerSoftwareVersion: getPlayerVersion(),
		playerInitTime: void 0,
		metadata: void 0
	};
	#MuxDataSdk = MuxDataExtension.defaultProps.MuxDataSdk;
	#pendingSync = null;
	#beaconCollectionDomain = MuxDataExtension.defaultProps.beaconCollectionDomain;
	#debug = MuxDataExtension.defaultProps.debug;
	#disableCookies = MuxDataExtension.defaultProps.disableCookies;
	#metadata = MuxDataExtension.defaultProps.metadata;
	#envKey = MuxDataExtension.defaultProps.envKey;
	#playerSoftwareName = MuxDataExtension.defaultProps.playerSoftwareName;
	#playerSoftwareVersion = MuxDataExtension.defaultProps.playerSoftwareVersion;
	#playerInitTime = MuxDataExtension.defaultProps.playerInitTime;
	#playerCreatedAt;
	#constructedAt = this.#generatePlayerInitTime();
	#media = null;
	#stopListening = null;
	#target = null;
	#monitoredSrc = null;
	#monitoredEngine = null;
	#engineHook = null;
	#viewSessionId;
	constructor(props = {}) {
		Object.assign(this, props);
	}
	/** @internal Player lifecycle; the player calls it. */
	connect(player) {
		this.#playerCreatedAt = player.initTime;
	}
	/** @internal Player lifecycle; the player calls it. */
	disconnect() {
		this.#playerCreatedAt = void 0;
	}
	/** @internal Player lifecycle; the player calls it. */
	attach({ media }) {
		if (this.#media === media) return;
		this.detach();
		this.#media = media;
		this.#stopListening = listen(media, "loadstart", this.#syncMonitor);
		this.#syncMonitor();
	}
	/** @internal Player lifecycle; the player calls it. */
	detach() {
		this.#destroyMonitor();
		this.#stopListening?.();
		this.#stopListening = null;
		this.#media = null;
		this.#target = null;
	}
	destroy() {
		this.detach();
	}
	get MuxDataSdk() {
		return this.#MuxDataSdk;
	}
	set MuxDataSdk(value) {
		if (this.#MuxDataSdk === value) return;
		this.#MuxDataSdk = value;
		this.#reinitialize();
	}
	get beaconCollectionDomain() {
		return this.#beaconCollectionDomain;
	}
	set beaconCollectionDomain(value) {
		if (this.#beaconCollectionDomain === value) return;
		this.#beaconCollectionDomain = value;
		this.#reinitialize();
	}
	get debug() {
		return this.#debug;
	}
	set debug(value) {
		if (this.#debug === value) return;
		this.#debug = value;
		this.#reinitialize();
	}
	get disableCookies() {
		return this.#disableCookies;
	}
	set disableCookies(value) {
		if (this.#disableCookies === value) return;
		this.#disableCookies = value;
		this.#reinitialize();
	}
	/**
	* Mux Data environment key. Omitted from the beacon when unset, which is the norm for Mux-hosted playback: the view
	* reports the Mux playback ID as its `video_id` (see {@link toVideoId}) and Mux attributes it to the owning
	* environment. Set this to monitor sources Mux doesn't host.
	*/
	get envKey() {
		return this.#envKey;
	}
	set envKey(value) {
		if (this.#envKey === value) return;
		this.#envKey = value;
		this.#target?.mux?.updateData(value ? { env_key: value } : {});
	}
	get playerSoftwareName() {
		return this.#playerSoftwareName;
	}
	set playerSoftwareName(value) {
		if (this.#playerSoftwareName === value) return;
		this.#playerSoftwareName = value;
		this.#target?.mux?.updateData(value ? { player_software_name: value } : {});
	}
	get playerSoftwareVersion() {
		return this.#playerSoftwareVersion;
	}
	set playerSoftwareVersion(value) {
		if (this.#playerSoftwareVersion === value) return;
		this.#playerSoftwareVersion = value;
		this.#target?.mux?.updateData(value ? { player_software_version: value } : {});
	}
	/** Epoch milliseconds the player was initialized. Defaults to when the player was created. */
	get playerInitTime() {
		return this.#playerInitTime ?? this.#playerCreatedAt ?? this.#constructedAt;
	}
	set playerInitTime(value) {
		if (this.#playerInitTime === value) return;
		this.#playerInitTime = value;
		const initTime = this.playerInitTime;
		this.#target?.mux?.updateData(initTime ? { player_init_time: initTime } : {});
	}
	get metadata() {
		return this.#metadata;
	}
	set metadata(value) {
		if (this.#metadata === value) return;
		this.#metadata = value;
		this.#target?.mux?.updateData(value ? { ...value } : {});
	}
	#destroyMonitor() {
		if (this.#target?.mux) {
			this.#target.mux.destroy();
			delete this.#target.mux;
		}
		this.#monitoredSrc = null;
		this.#monitoredEngine = null;
		this.#engineHook = null;
	}
	/**
	* Full re-monitor, reserved for options that are baked into `monitor()` itself (the SDK, beacon routing, debug,
	* cookies). Source and engine changes go through {@link #syncMonitor} instead, which keeps the monitor — and its view
	* — alive.
	*/
	#reinitialize = () => {
		this.#destroyMonitor();
		this.#syncMonitor();
	};
	/**
	* Reconcile the monitor with the media's current state. Called on every `loadstart`, but the event is only a hint:
	* the media's element, `src`, and `engine` are compared against what the monitor already reflects, so a same-video
	* `load()` (remote playback engaging, an engine rebuild, a MediaSource re-attach) is a no-op, a video change becomes
	* a `videochange` on the live monitor, and only a missing monitor — or a new element behind the media — starts a new
	* one.
	*/
	#syncMonitor = () => {
		this.#sync();
	};
	async #sync() {
		if (this.#pendingSync) return;
		await (this.#pendingSync = Promise.resolve());
		this.#pendingSync = null;
		const media = this.#media;
		if (!media) return;
		const target = getMediaElement(media);
		if (target !== this.#target) {
			this.#destroyMonitor();
			this.#target = target;
		}
		if (!this.MuxDataSdk || !target) return;
		const mux = target.mux;
		if (!mux || mux.deleted) {
			this.#monitor(target, media);
			return;
		}
		this.#syncEngineHook(mux, engineOf(media));
		const src = srcOf(media);
		if (src === this.#monitoredSrc) return;
		if (!src) return;
		const isFirstSource = !this.#monitoredSrc;
		this.#monitoredSrc = src;
		if (isFirstSource) {
			mux.updateData(this.#videoData(media));
			return;
		}
		mux.emit("videochange", this.#videoData(media));
	}
	/** Keep engine telemetry hooked to the engine actually playing, without restarting the monitor. */
	#syncEngineHook(mux, engine) {
		if (engine === this.#monitoredEngine) return;
		if (this.#engineHook === "hlsjs") mux.removeHLSJS();
		if (this.#engineHook === "dashjs") mux.removeDashJS();
		const options = toMuxDataEngineOptions(engine);
		if (options.hlsjs && options.Hls) mux.addHLSJS({
			hlsjs: options.hlsjs,
			Hls: options.Hls
		});
		if (options.dashjs) mux.addDashJS({ dashjs: options.dashjs });
		this.#trackEngine(engine, options);
	}
	/** Record which engine the monitor reflects and which telemetry hook carries it. */
	#trackEngine(engine, options) {
		this.#monitoredEngine = engine ?? null;
		this.#engineHook = options.hlsjs ? "hlsjs" : options.dashjs ? "dashjs" : null;
	}
	#monitor(target, media) {
		const { debug, beaconCollectionDomain, disableCookies, envKey: env_key, playerSoftwareName: player_software_name, playerSoftwareVersion: player_software_version, playerInitTime: player_init_time } = this;
		const engine = engineOf(media);
		const engineOptions = toMuxDataEngineOptions(engine);
		this.#monitoredSrc = srcOf(media);
		this.#trackEngine(engine, engineOptions);
		this.MuxDataSdk?.monitor(target, {
			debug,
			...beaconCollectionDomain ? { beaconCollectionDomain } : {},
			...disableCookies ? { disableCookies } : {},
			...engineOptions,
			data: {
				...env_key ? { env_key } : {},
				...player_software_name ? { player_software_name } : {},
				...player_software_name ? { player_software: player_software_name } : {},
				...player_software_version ? { player_software_version } : {},
				...player_init_time ? { player_init_time } : {},
				...this.#videoData(media)
			}
		});
	}
	/**
	* The video-scoped beacon data: the session id, the derived `video_id`, and the caller's metadata, which may override
	* both. Built fresh per use — the caller's `metadata` object is never mutated.
	*/
	#videoData(media) {
		const metadata = this.metadata ?? {};
		const view_session_id = metadata.view_session_id ?? (this.#viewSessionId ??= this.MuxDataSdk?.utils.generateUUID());
		const video_id = toVideoId({
			metadata,
			src: srcOf(media)
		});
		const derived = {};
		if (view_session_id) derived.view_session_id = view_session_id;
		if (video_id) derived.video_id = video_id;
		return {
			...derived,
			...metadata
		};
	}
	#generatePlayerInitTime() {
		if (!this.MuxDataSdk) return void 0;
		return this.MuxDataSdk.utils.now();
	}
};
function toVideoId(props) {
	if (props.metadata?.video_id) return props.metadata.video_id;
	if (!isMuxVideoSrc(props)) return props.src;
	return toPlaybackIdFromSrc(props.src) ?? props.src;
}
function toPlaybackIdFromSrc(src) {
	if (!src?.startsWith("https://stream.")) return void 0;
	const [playbackId] = new URL(src).pathname.slice(1).split(/\.m3u8|\//);
	return playbackId || void 0;
}
function isMuxVideoSrc({ src }) {
	if (typeof src !== "string") return false;
	const base = window?.location.href;
	return new URL(src, base).hostname.toLocaleLowerCase().includes(MUX_VIDEO_DOMAIN);
}
//#endregion
export { MuxDataExtension, isMuxVideoSrc, toPlaybackIdFromSrc, toVideoId };
